import { useId } from "react";

/**
 * Ett formulärfält med label, fel och tillgänglighet på ett ställe.
 *
 * Formulären hade tidigare placeholders i stället för labels. En
 * placeholder försvinner så fort man börjar skriva, läses inte upp av alla
 * skärmläsare som ett fältnamn, och lämnar den som zoomat in utan att veta
 * vad de fyller i.
 *
 * Felet kopplas till inputen med aria-describedby och aria-invalid, så att
 * en skärmläsare läser upp det tillsammans med fältet i stället för att
 * lämna det som löptext någon annanstans på sidan. role="alert" gör att det
 * annonseras när det dyker upp.
 */
export function Field({
  label,
  error,
  hint,
  required,
  children,
}: {
  label: string;
  error?: string;
  hint?: string;
  required?: boolean;
  children: (props: {
    id: string;
    "aria-invalid": boolean | undefined;
    "aria-describedby": string | undefined;
    required: boolean | undefined;
  }) => React.ReactNode;
}) {
  const id = useId();
  const errorId = `${id}-error`;
  const hintId = `${id}-hint`;

  const describedBy = [error ? errorId : null, hint ? hintId : null]
    .filter(Boolean)
    .join(" ");

  return (
    <div className="form-field">
      <label className="form-label" htmlFor={id}>
        {label}
        {required && (
          <span className="form-required" aria-hidden="true">
            {" *"}
          </span>
        )}
      </label>

      {children({
        id,
        "aria-invalid": error ? true : undefined,
        "aria-describedby": describedBy || undefined,
        required: required || undefined,
      })}

      {hint && (
        <p className="form-hint" id={hintId}>
          {hint}
        </p>
      )}

      {error && (
        <p className="form-error" id={errorId} role="alert">
          {error}
        </p>
      )}
    </div>
  );
}

/**
 * Formulärets sammanfattande fel — det som inte hör till ett enskilt fält.
 *
 * Visas bara när servern inte pekat ut några fält. Annars står felet redan
 * under rätt input, och en sammanfattning ovanför skulle säga samma sak två
 * gånger.
 */
export function FormError({ message }: { message?: string }) {
  if (!message) return null;

  return (
    <p className="form-error form-error-summary" role="alert">
      {message}
    </p>
  );
}

/** Kvitto efter en lyckad åtgärd. aria-live så det annonseras. */
export function FormSuccess({ message }: { message?: string }) {
  if (!message) return null;

  return (
    <p className="form-success" role="status">
      {message}
    </p>
  );
}
